import React, { useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { BackButton, FormGroupControl, Loading, NoData, LoadingError } from '@components/Base';
import { isNull, isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { InsuranceTypes } from '@models/InsuranceType';
import { selectHealthPolicyClaim } from './selector';
import { useInitialValues } from './hook';
import { policyFetch, healthClaimCreate, HealthClaimCreatePayload } from './action';
import { FormValues, formValuesSchema } from './schema';
import { PolicyOverviewCardBody } from './policy-overview-card-body';
import { PaymentInfoCardBody } from './payment-info-card-body';
import { ClaimCardBody } from './claim-card-body';

const HealthPolicyClaim = () => {
  const { t } = useTranslation();
  const { policy } = useSelector(selectHealthPolicyClaim);
  const { orderableId, orderableType } = useParams();
  const dispatch = useDispatch();
  const isLoaded = !isUndefined(policy);
  const isError = isNull(policy);
  const hasPolicy = isLoaded && !isNull(policy);
  const initialValues = useInitialValues();

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: HealthClaimCreatePayload = {
      ...values,
      healthOrderId: Number(orderableId),
      insuranceType: InsuranceTypes.Health,
    };
    dispatch(healthClaimCreate(payload));
    setSubmitting(false);
  };

  useEffect(() => {
    dispatch(policyFetch({ orderableId, orderableType }));
  }, [orderableId, orderableType]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasPolicy && <NoData />}

      {initialValues && (
        <Card>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={formValuesSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Card.Header className="fs-5 fw-bold">{t('common:claimForm')}</Card.Header>

                <PolicyOverviewCardBody />
                <PaymentInfoCardBody />
                <ClaimCardBody />

                <Card.Body>
                  <FormGroupControl
                    type="checkbox"
                    name="acceptTermsAndConditions"
                    label={t('carInsuranceClaimForm.acceptTermsAndConditions')}
                    bsPrefix="form-check-input"
                    groupProps={{ className: 'form-check form-check-inline' }}
                  />
                </Card.Body>

                <Card.Footer className="bg-white">
                  <div className="text-center my-4">
                    <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                      {t('common:submit')}
                    </Button>
                  </div>
                </Card.Footer>
              </Form>
            )}
          </Formik>
        </Card>
      )}
    </>
  );
};

export { HealthPolicyClaim };
